import { useState } from "react";
import { Fingerprint, Upload, ClipboardPaste, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCaptureFlow } from "@/components/attendance/CaptureModal";
import { SCANNER_STATE } from "@/lib/mantra";

const fileToDataUrl = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

export default function FingerSlot({ index, value, onChange }) {
  const flow = useCaptureFlow();
  const [busy, setBusy] = useState(false);

  const capture = async () => {
    setBusy(true);
    const r = await flow.capture();
    setBusy(false);
    if (r) onChange({ ...r, source: r.mode });
    else if (flow.error) toast.error(`Finger ${index}: ${flow.error}`);
  };
  const attachImage = (image, label) => {
    if (value?.rdOnly) { onChange({ ...value, image, imageSource: label }); toast.success(`Finger ${index}: image attached to RD capture (Q ${value.quality ?? "—"}%)`); return; }
    onChange({ mode: "image_upload", template: null, image, quality: null, device: { model: label, serial: null, mode: "image_upload" } });
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    attachImage(await fileToDataUrl(file), "Uploaded image");
    e.target.value = "";
  };
  const paste = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const it of items) {
        const type = it.types.find((t) => t.startsWith("image/"));
        if (type) { const blob = await it.getType(type); attachImage(await fileToDataUrl(blob), "Pasted image"); return; }
      }
      toast.error("No image found in clipboard");
    } catch { toast.error("Clipboard access denied — allow clipboard permission or use Upload"); }
  };
  const capturing = flow.phase === SCANNER_STATE.CAPTURING || busy;

  return (
    <div className="flex flex-col items-center gap-3" data-testid={`finger-slot-${index}`}>
      <div className={`relative w-36 h-36 rounded-2xl border-2 flex items-center justify-center bg-white overflow-hidden ${value ? "border-emerald-500" : "border-dashed border-slate-300"} ${capturing ? "animate-pulse border-sky-400" : ""}`}>
        {value?.image ? <img src={value.image} alt={`Finger ${index} fingerprint`} className="w-full h-full object-contain" /> :
          value ? <div className="text-center text-emerald-700 text-[11px] leading-tight px-2" title="MFS110 L1 RD Service returns an encrypted PID only — the device does not release the fingerprint image. Use Upload/Paste to attach an image."><Fingerprint className="w-10 h-10 mx-auto" />RD captured ✓<br /><span className="text-slate-500">L1 device gives no image</span><br /><span className="text-indigo-600">Upload/Paste to attach</span></div> :
          <Fingerprint className="w-14 h-14 text-rose-400" strokeWidth={1.2} />}
        {value && <button onClick={() => onChange(null)} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 text-slate-500 hover:text-red-600" aria-label="Clear"><X className="w-3.5 h-3.5" /></button>}
        {value?.quality != null && <span className="absolute bottom-1 left-1 text-[10px] bg-emerald-700 text-white rounded px-1.5">Q {value.quality}%</span>}
      </div>
      <p className="text-xs text-slate-500">Finger {index}</p>
      <div className="flex gap-1.5">
        <Button size="sm" data-testid={`slot-capture-${index}`} onClick={capture} disabled={capturing} className="h-8 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-3">{capturing ? "…" : "Capture"}</Button>
        <label className="h-8 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs px-3 inline-flex items-center gap-1 cursor-pointer" data-testid={`slot-upload-${index}`}><Upload className="w-3 h-3" />Upload<input type="file" accept="image/*" className="hidden" onChange={upload} /></label>
        <Button size="sm" data-testid={`slot-paste-${index}`} onClick={paste} className="h-8 rounded-lg bg-amber-400 hover:bg-amber-500 text-slate-900 text-xs px-3"><ClipboardPaste className="w-3 h-3 mr-1" />Paste</Button>
      </div>
    </div>
  );
}
